import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  Search,
  X as XIcon,
} from 'lucide-react';
import type {
  DiscoveryEvent,
  ProductDetail,
  ProductStatus,
  RoiGrade,
  Supplier,
} from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../design';
import { ApiCallError } from '../lib/api';
import { useProductDetail, useUpdateProduct } from '../lib/products';
import { useCreateSupplier, usePinAsin, useSuppliers, useSuppliersForAsin } from '../lib/suppliers';
import {
  eventProgressLabel,
  previewDiscovery,
  streamDiscovery,
  useCandidates,
  useDiscoveryRuns,
  useDismissCandidate,
  useInvalidateDiscoveryRuns,
  usePromoteCandidate,
} from '../lib/discovery';
import type { DiscoveryPreviewResponse } from '@sourcito/schemas';

const STATUS_OPTIONS: Array<{ value: ProductStatus; label: string }> = [
  { value: 'pending', label: 'Pending — not yet reviewed' },
  { value: 'interested', label: 'Interested — worth a look' },
  { value: 'pursuing', label: 'Pursuing — actively researching' },
  { value: 'passed', label: 'Passed — not for me' },
];

export function ProductDetailPage() {
  const { asin } = useParams({ from: '/products/$asin' });
  const { data: product, isLoading, isError, error, refetch } = useProductDetail(asin);
  const update = useUpdateProduct(asin);

  // Local form state. Hydrated from server on load and after each successful save.
  const [status, setStatus] = useState<ProductStatus>('pending');
  const [notes, setNotes] = useState('');
  const [supplierCostInput, setSupplierCostInput] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!product) return;
    setStatus(product.status);
    setNotes(product.notes ?? '');
    setSupplierCostInput(
      product.manualSupplierCostCents !== null
        ? (product.manualSupplierCostCents / 100).toFixed(2)
        : ''
    );
  }, [product]);

  const dirty = useMemo(() => {
    if (!product) return false;
    const trimmedNotes = notes.trim();
    const serverNotes = product.notes ?? '';
    if (trimmedNotes !== serverNotes) return true;
    if (status !== product.status) return true;

    const parsedCost = parseSupplierCostCents(supplierCostInput);
    if (parsedCost === 'invalid') return false;
    if (parsedCost !== product.manualSupplierCostCents) return true;
    return false;
  }, [product, status, notes, supplierCostInput]);

  async function handleSave() {
    if (!product || !dirty) return;
    setFeedback(null);

    const payload: {
      status?: ProductStatus;
      notes?: string | null;
      manualSupplierCostCents?: number | null;
    } = {};

    if (status !== product.status) payload.status = status;
    const trimmedNotes = notes.trim();
    if (trimmedNotes !== (product.notes ?? '')) {
      payload.notes = trimmedNotes === '' ? null : trimmedNotes;
    }
    const parsedCost = parseSupplierCostCents(supplierCostInput);
    if (parsedCost !== 'invalid' && parsedCost !== product.manualSupplierCostCents) {
      payload.manualSupplierCostCents = parsedCost;
    }

    try {
      await update.mutateAsync(payload);
      setFeedback('Saved.');
      refetch();
    } catch (err) {
      const msg = err instanceof ApiCallError ? err.message : (err as Error).message;
      setFeedback(`Save failed: ${msg}`);
    }
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/products"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight font-mono">{asin}</h1>
              <p className="text-xs text-fg-tertiary">
                {product?.title ?? (isLoading ? 'Loading…' : 'Product detail')}
              </p>
            </div>
          </div>
          {product && (
            <a
              href={`https://www.amazon.com/dp/${product.asin}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-accent-fg hover:underline inline-flex items-center gap-1"
            >
              View on Amazon <ExternalLink className="size-3.5" />
            </a>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10 space-y-6">
        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">Failed to load: {(error as Error).message}</p>
          </Card>
        )}

        {product && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <KeepaSnapshotCard product={product} />
              <RoiCard product={product} />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Operator workflow</CardTitle>
                <CardDescription>
                  Status, notes, and a manual supplier-cost override that recomputes ROI on save.
                  All changes are audit-logged.
                </CardDescription>
              </CardHeader>

              <div className="space-y-5">
                <FormField label="Status" htmlFor="status">
                  <Select value={status} onValueChange={(v) => setStatus(v as ProductStatus)}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  label="Manual supplier cost (USD)"
                  htmlFor="supplier-cost"
                  helper={`Overrides the fee profile's markup default. Leave blank to use the default. Current sale price: ${
                    product.currentPriceCents !== null
                      ? `$${(product.currentPriceCents / 100).toFixed(2)}`
                      : 'unknown'
                  }.`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-fg-tertiary text-sm">$</span>
                    <Input
                      id="supplier-cost"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="0.00"
                      value={supplierCostInput}
                      onChange={(e) => setSupplierCostInput(e.target.value)}
                    />
                  </div>
                </FormField>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    rows={4}
                    className="mt-1.5 w-full px-3 py-2 text-sm bg-surface-elev border border-border-default rounded-md placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent resize-none transition-colors duration-fast font-mono"
                    placeholder="Supplier contact, MOQ negotiated, prep notes, etc."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={2000}
                  />
                  <p className="text-xs text-fg-tertiary mt-1 tabular-nums">
                    {notes.length} / 2000
                  </p>
                </div>

                {feedback && (
                  <p
                    className={`text-sm ${feedback.startsWith('Saved') ? 'text-positive' : 'text-negative'}`}
                  >
                    {feedback}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={!dirty || update.isPending}
                    loading={update.isPending}
                    leadingIcon={<Check className="size-4" />}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Card>

            <SuppliersForAsinPanel asin={asin} brand={product.brand} />
          </>
        )}
      </main>
    </div>
  );
}

function SuppliersForAsinPanel({ asin, brand }: { asin: string; brand: string | null }) {
  const { data, isLoading } = useSuppliersForAsin(asin);
  const { data: candidates } = useCandidates(asin, 'pending');
  const [pinDialogOpen, setPinDialogOpen] = useState(false);

  // Discovery state — preview is the cheap pre-flight, stream is the run.
  // Only one run / preview in flight at a time per page mount.
  const [preview, setPreview] = useState<DiscoveryPreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runsExpanded, setRunsExpanded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const invalidate = useInvalidateDiscoveryRuns(asin);
  const { data: recentRuns } = useDiscoveryRuns(asin);

  const suppliers = data?.suppliers ?? [];
  const pendingCandidates = candidates ?? [];

  async function startPreview() {
    setProgress([]);
    setRunError(null);
    setPreviewError(null);
    setPreviewing(true);
    try {
      const p = await previewDiscovery(asin);
      setPreview(p);
    } catch (err) {
      setPreviewError((err as Error).message ?? 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  }

  async function startDiscovery() {
    if (!preview) return;
    setProgress([]);
    setRunError(null);
    setRunning(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      for await (const ev of streamDiscovery(asin, { signal: controller.signal })) {
        setProgress((prev) => [...prev, eventProgressLabel(ev as DiscoveryEvent)]);
        if (ev.type === 'run_failed') setRunError(ev.error);
      }
    } catch (err) {
      setRunError((err as Error).message ?? 'Discovery failed.');
    } finally {
      setRunning(false);
      abortRef.current = null;
      setPreview(null);
      invalidate();
    }
  }

  function discardPreview() {
    setPreview(null);
    setPreviewError(null);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle>Suppliers for this ASIN</CardTitle>
            <CardDescription>
              Run a discovery search to surface candidate suppliers, then promote the ones worth
              pursuing. Uses your OpenRouter + Tavily keys (BYOK) — both required.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<Search className="size-4" />}
              onClick={startPreview}
              loading={previewing}
              disabled={previewing || running || preview !== null}
            >
              Find supplier
            </Button>
            <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
              <Button
                size="sm"
                leadingIcon={<Plus className="size-4" />}
                onClick={() => setPinDialogOpen(true)}
              >
                Pin supplier
              </Button>
              <DialogContent>
                <PinToSupplierForm
                  asin={asin}
                  brand={brand}
                  onClose={() => setPinDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>

      {/* Preview card (shown after clicking Find supplier; user clicks Run to commit) */}
      {previewError && (
        <div className="border border-negative/30 bg-negative/[0.06] rounded-sm p-3 mb-4">
          <p className="text-xs text-negative">{previewError}</p>
        </div>
      )}
      {preview && (
        <div className="border border-border-subtle rounded-sm p-3 bg-surface-1/50 mb-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="text-xs font-medium text-fg-secondary">Discovery preview</p>
              <p className="text-xs text-fg-tertiary mt-0.5">
                {preview.queries.length} planned{' '}
                {preview.queries.length === 1 ? 'query' : 'queries'} ·{' '}
                <span className="tabular-nums">~{preview.estimatedTavilyCredits}</span> Tavily
                credits ·{' '}
                <span className="tabular-nums">
                  ~
                  {(
                    preview.generatorUsage.inputTokens +
                    preview.generatorUsage.outputTokens +
                    preview.estimatedParserTokens.inputTokens +
                    preview.estimatedParserTokens.outputTokens
                  ).toLocaleString()}
                </span>{' '}
                LLM tokens
                {preview.tavilyBalance && preview.tavilyBalance.remaining !== null && (
                  <>
                    {' '}
                    · balance{' '}
                    <span className="tabular-nums">
                      {preview.tavilyBalance.remaining.toLocaleString()}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="ghost" onClick={discardPreview} disabled={running}>
                Cancel
              </Button>
              <Button size="sm" onClick={startDiscovery} loading={running} disabled={running}>
                Run
              </Button>
            </div>
          </div>
          <ul className="text-xs font-mono text-fg-tertiary space-y-0.5 mt-2 max-h-32 overflow-auto">
            {preview.queries.map((q, i) => (
              <li key={i}>
                <span className="inline-block w-6 text-fg-secondary">T{q.tier}</span>
                {q.query}
              </li>
            ))}
          </ul>
          {preview.tavilyBalance &&
            preview.tavilyBalance.remaining !== null &&
            preview.tavilyBalance.remaining < preview.estimatedTavilyCredits && (
              <p className="text-xs text-warning mt-2">
                Estimated cost ({preview.estimatedTavilyCredits}) exceeds Tavily balance (
                {preview.tavilyBalance.remaining}). The run may fail mid-way.
              </p>
            )}
        </div>
      )}

      {/* Discovery progress strip */}
      {(running || progress.length > 0 || runError) && (
        <div className="border border-border-subtle rounded-sm p-3 bg-surface-1/50 mb-4">
          <p className="text-xs font-medium text-fg-secondary mb-1.5">Discovery progress</p>
          <ul className="text-xs font-mono text-fg-tertiary space-y-0.5 max-h-32 overflow-auto">
            {progress.map((line, i) => (
              <li key={i}>· {line}</li>
            ))}
            {running && <li className="text-fg-secondary">…</li>}
          </ul>
          {runError && <p className="text-xs text-negative mt-2">{runError}</p>}
        </div>
      )}

      {/* Candidate review */}
      {pendingCandidates.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-fg-secondary mb-2">
            Candidates pending review ({pendingCandidates.length})
          </p>
          <ul className="space-y-2">
            {pendingCandidates.map((c) => (
              <CandidateRow key={c.id} asin={asin} candidate={c} />
            ))}
          </ul>
        </div>
      )}

      {/* Pinned suppliers */}
      <p className="text-xs font-medium text-fg-secondary mb-2">
        Pinned suppliers ({suppliers.length})
      </p>
      {isLoading && <p className="text-sm text-fg-secondary">Loading suppliers…</p>}
      {!isLoading && suppliers.length === 0 && (
        <p className="text-sm text-fg-secondary">No suppliers pinned to this ASIN yet.</p>
      )}
      {suppliers.length > 0 && (
        <ul className="divide-y divide-border-subtle -mx-4">
          {suppliers.map((s) => (
            <li key={s.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to="/suppliers/$id"
                  params={{ id: s.id }}
                  className="font-medium text-fg-primary hover:text-accent-fg"
                >
                  {s.name}
                </Link>
                {s.website && <p className="text-xs text-fg-tertiary truncate">{s.website}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="neutral">{s.status}</Badge>
                <Link
                  to="/suppliers/$id"
                  params={{ id: s.id }}
                  className="text-fg-tertiary hover:text-fg-primary"
                  aria-label={`Open ${s.name}`}
                >
                  <ChevronRight className="size-4" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Recent discovery runs (collapsible) */}
      {recentRuns && recentRuns.length > 0 && (
        <div className="mt-4 border-t border-border-subtle pt-3">
          <button
            type="button"
            onClick={() => setRunsExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium text-fg-secondary hover:text-fg-primary"
          >
            <ChevronRight
              className={`size-3 transition-transform duration-fast ${runsExpanded ? 'rotate-90' : ''}`}
            />
            Recent runs ({recentRuns.length})
          </button>
          {runsExpanded && (
            <ul className="mt-2 space-y-1.5 text-xs font-mono">
              {recentRuns.slice(0, 5).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 text-fg-tertiary">
                  <span>
                    <Badge
                      variant={
                        r.status === 'succeeded'
                          ? 'positive'
                          : r.status === 'failed'
                            ? 'negative'
                            : 'neutral'
                      }
                    >
                      {r.status}
                    </Badge>{' '}
                    {new Date(r.startedAt).toLocaleString()}
                  </span>
                  <span className="tabular-nums">
                    {r.candidateCount} cand · {r.tavilyCreditsUsed} cr ·{' '}
                    {(r.llmInputTokens + r.llmOutputTokens).toLocaleString()} tok
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function CandidateRow({
  asin,
  candidate,
}: {
  asin: string;
  candidate: import('@sourcito/schemas').SupplierCandidate;
}) {
  const promote = usePromoteCandidate(asin);
  const dismiss = useDismissCandidate(asin);

  return (
    <li className="border border-border-subtle rounded-sm p-3 hover:border-border-default transition-colors duration-fast">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{candidate.companyName}</span>
            <Badge variant={candidate.tier === 1 ? 'positive' : 'accent'}>T{candidate.tier}</Badge>
          </div>
          {candidate.website && (
            <p className="text-xs text-fg-tertiary mt-0.5">{candidate.website}</p>
          )}
          {candidate.contactEmail && (
            <p className="text-xs text-fg-secondary mt-0.5">{candidate.contactEmail}</p>
          )}
          <p className="text-xs text-fg-secondary mt-1.5 italic">{candidate.rationale}</p>
          <a
            href={candidate.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-accent-fg hover:underline mt-1 inline-flex items-center gap-1"
          >
            Source <ExternalLink className="size-3" />
          </a>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm"
            leadingIcon={<Check className="size-3.5" />}
            loading={promote.isPending}
            onClick={() => promote.mutate({ id: candidate.id })}
          >
            Promote
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<XIcon className="size-3.5" />}
            loading={dismiss.isPending}
            onClick={() => dismiss.mutate(candidate.id)}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </li>
  );
}

function PinToSupplierForm({
  asin,
  brand,
  onClose,
}: {
  asin: string;
  brand: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: allSuppliers } = useSuppliers();
  const create = useCreateSupplier();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pinNote, setPinNote] = useState('');
  const [newName, setNewName] = useState(brand ?? '');
  const [newWebsite, setNewWebsite] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pinToExisting = usePinAsin(selectedId ?? '00000000-0000-0000-0000-000000000000');

  async function submit() {
    setError(null);
    try {
      if (mode === 'existing') {
        if (!selectedId) {
          setError('Pick a supplier to pin to.');
          return;
        }
        await pinToExisting.mutateAsync({
          asin,
          notes: pinNote.trim() || null,
        });
      } else {
        if (!newName.trim()) {
          setError('Supplier name is required.');
          return;
        }
        const created = await create.mutateAsync({
          name: newName.trim(),
          website: newWebsite.trim() || null,
          status: 'lead',
        });
        // Pin the ASIN to the freshly created supplier. We bypass the
        // `usePinAsin(id)` hook because hooks can't be created mid-call;
        // manually invalidate the affected queries so the panel updates.
        const res = await fetch(`/api/suppliers/${created.id}/asins`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ asin, notes: pinNote.trim() || null }),
        });
        if (!res.ok) throw new Error(`Pin failed (${res.status})`);
        queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        queryClient.invalidateQueries({ queryKey: ['suppliers-for-asin', asin] });
      }
      onClose();
    } catch (err) {
      setError((err as Error).message ?? 'Failed to pin');
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <DialogHeader>
        <DialogTitle>Pin a supplier to {asin}</DialogTitle>
        <DialogDescription>
          Attach this product to an existing supplier or create a new one on the fly.
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMode('existing')}
          className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast ${
            mode === 'existing'
              ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
              : 'border-border-subtle text-fg-secondary hover:text-fg-primary'
          }`}
        >
          Existing supplier
        </button>
        <button
          type="button"
          onClick={() => setMode('new')}
          className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast ${
            mode === 'new'
              ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
              : 'border-border-subtle text-fg-secondary hover:text-fg-primary'
          }`}
        >
          New supplier
        </button>
      </div>

      {mode === 'existing' && (
        <FormField label="Supplier" htmlFor="supplier-select">
          {allSuppliers && allSuppliers.length > 0 ? (
            <Select value={selectedId ?? undefined} onValueChange={(v) => setSelectedId(v)}>
              <SelectTrigger id="supplier-select">
                <SelectValue placeholder="Pick a supplier…" />
              </SelectTrigger>
              <SelectContent>
                {allSuppliers.map((s: Supplier) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-fg-tertiary">
              No suppliers exist yet. Switch to "New supplier" to create one.
            </p>
          )}
        </FormField>
      )}

      {mode === 'new' && (
        <>
          <FormField label="Name" htmlFor="new-name" required>
            <Input
              id="new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={brand ?? 'Supplier name'}
              autoFocus
            />
          </FormField>
          <FormField label="Website" htmlFor="new-website">
            <Input
              id="new-website"
              value={newWebsite}
              onChange={(e) => setNewWebsite(e.target.value)}
              placeholder="anker.com"
            />
          </FormField>
        </>
      )}

      <FormField label="Notes for this pin (optional)" htmlFor="pin-note">
        <Input
          id="pin-note"
          value={pinNote}
          onChange={(e) => setPinNote(e.target.value)}
          placeholder="e.g. flagship SKU, target $X cost"
        />
      </FormField>

      {error && (
        <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
          <p className="text-sm text-negative">{error}</p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          loading={create.isPending || pinToExisting.isPending}
          disabled={mode === 'existing' && !selectedId}
        >
          Pin
        </Button>
      </DialogFooter>
    </form>
  );
}

function KeepaSnapshotCard({ product }: { product: ProductDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Keepa snapshot</CardTitle>
        <CardDescription>
          As of{' '}
          <time dateTime={product.keepaRefreshedAt}>
            {new Date(product.keepaRefreshedAt).toLocaleString()}
          </time>
        </CardDescription>
      </CardHeader>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm tabular-nums">
        <Row label="Title" value={product.title ?? '—'} />
        <Row label="Brand" value={product.brand ?? '—'} />
        <Row label="Category" value={product.category ?? '—'} />
        <Row
          label="Sale price"
          value={
            product.currentPriceCents !== null
              ? `$${(product.currentPriceCents / 100).toFixed(2)}`
              : '—'
          }
        />
        <Row label="BSR" value={product.currentBsr?.toLocaleString() ?? '—'} />
        <Row label="Sales/mo" value={product.monthlySalesEstimate?.toLocaleString() ?? '—'} />
        <Row label="Reviews" value={product.reviewCount?.toLocaleString() ?? '—'} />
        <Row label="Rating" value={product.rating?.toFixed(1) ?? '—'} />
        <Row
          label="Amazon as seller"
          value={product.amazonIsSeller === null ? '—' : product.amazonIsSeller ? 'Yes' : 'No'}
        />
      </dl>
    </Card>
  );
}

function RoiCard({ product }: { product: ProductDetail }) {
  const roiPct =
    product.estimatedRoiPct === null ? null : (product.estimatedRoiPct * 100).toFixed(1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>ROI scoring</CardTitle>
        <CardDescription>
          Computed against fee profile v{product.feeProfileVersion ?? '?'} at last refresh.
        </CardDescription>
      </CardHeader>
      <div className="space-y-3">
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-semibold tabular-nums">{roiPct ?? '—'}%</span>
          {product.roiGrade && <GradeBadge grade={product.roiGrade} />}
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm tabular-nums">
          <Row
            label="Sale price"
            value={
              product.currentPriceCents !== null
                ? `$${(product.currentPriceCents / 100).toFixed(2)}`
                : '—'
            }
          />
          <Row
            label="Manual supplier cost"
            value={
              product.manualSupplierCostCents !== null ? (
                `$${(product.manualSupplierCostCents / 100).toFixed(2)}`
              ) : (
                <span className="text-fg-tertiary italic">using profile default</span>
              )
            }
          />
          <Row
            label="Landed cost"
            value={
              product.estimatedLandedCostCents !== null
                ? `$${(product.estimatedLandedCostCents / 100).toFixed(2)}`
                : '—'
            }
          />
        </dl>
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-fg-tertiary text-xs uppercase tracking-wider">{label}</dt>
      <dd className="text-fg-primary">{value}</dd>
    </>
  );
}

function GradeBadge({ grade }: { grade: RoiGrade }) {
  const variant =
    grade === 'A' || grade === 'B' ? 'positive' : grade === 'C' ? 'warning' : 'negative';
  return (
    <Badge variant={variant} className="text-base">
      {grade}
    </Badge>
  );
}

/**
 * Parses a "12.34" or "12" or "" string into integer cents.
 * Returns 'invalid' for unparseable input, null for empty.
 */
function parseSupplierCostCents(raw: string): number | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return 'invalid';
  return Math.round(parsed * 100);
}
